import React from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  opacity: ${props => props.visible ? 1 : 0};
  pointer-events: ${props => props.visible ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

const ModalContent = styled.div`
  background: #222;
  border-radius: 8px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  transform: ${props => props.visible ? 'scale(1)' : 'scale(0.9)'};
  transition: transform 0.3s ease;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
`;

const WarningIcon = styled.div`
  font-size: 2.5rem;
  color: #ff4444;
`;

const ModalTitle = styled.h2`
  color: #fff;
  margin: 0;
  font-size: 1.5rem;
`;

const ModalBody = styled.div`
  color: #ccc;
  margin-bottom: 25px;
  line-height: 1.6;
`;

const ItemInfo = styled.div`
  background: #1a1a1a;
  padding: 15px;
  border-radius: 6px;
  margin: 15px 0;
  border-left: 4px solid #ff4444;
`;

const ItemDetail = styled.div`
  margin: 5px 0;
  color: #fff;

  strong {
    color: #aaa;
    margin-right: 8px;
  }
`;

const ImagePreview = styled.img`
  max-width: 100px;
  max-height: 100px;
  object-fit: cover;
  border-radius: 4px;
  margin-top: 10px;
`;

const BulkItemsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  background: #1a1a1a;
  padding: 10px;
  border-radius: 4px;
  margin: 10px 0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #111;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #555;
    border-radius: 4px;
  }
`;

const BulkItem = styled.div`
  padding: 8px;
  border-bottom: 1px solid #333;
  color: #ccc;

  &:last-child {
    border-bottom: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 6px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: #444;
  color: #fff;

  &:hover:not(:disabled) {
    background: #555;
  }
`;

const DeleteButton = styled(Button)`
  background: #ff4444;
  color: #fff;

  &:hover:not(:disabled) {
    background: #cc0000;
  }
`;

function DeleteConfirmationModal({
  visible,
  item,
  items,
  isBulk,
  itemCount,
  onConfirm,
  onCancel
}) {
  if (!visible) return null;

  return (
    <ModalOverlay visible={visible} onClick={onCancel}>
      <ModalContent visible={visible} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <WarningIcon>⚠️</WarningIcon>
          <ModalTitle>
            {isBulk ? `Delete ${itemCount} Items?` : 'Delete Item?'}
          </ModalTitle>
        </ModalHeader>

        <ModalBody>
          {isBulk ? (
            <>
              <p>You are about to delete <strong>{itemCount}</strong> item(s).</p>
              <p>The image files will be moved to the trash folder. This action cannot be undone from this interface.</p>

              {items && items.length > 0 && (
                <BulkItemsList>
                  {items.map((itm, index) => (
                    <BulkItem key={itm.id || index}>
                      {itm.name || itm.title || `Item ${index + 1}`}
                    </BulkItem>
                  ))}
                </BulkItemsList>
              )}
            </>
          ) : item ? (
            <>
              <p>You are about to delete this item:</p>

              <ItemInfo>
                <ItemDetail>
                  <strong>Name:</strong>
                  {item.name || item.title || 'Untitled'}
                </ItemDetail>
                {item.price && (
                  <ItemDetail>
                    <strong>Price:</strong>
                    ${item.price}
                  </ItemDetail>
                )}
                {item.image && (
                  <ImagePreview src={item.image} alt={item.name || 'Item'} />
                )}
              </ItemInfo>

              <p>The image file will be moved to the trash folder. This action cannot be undone from this interface.</p>
            </>
          ) : (
            <p>Are you sure you want to delete this item?</p>
          )}
        </ModalBody>

        <ButtonGroup>
          <CancelButton onClick={onCancel}>
            Cancel
          </CancelButton>
          <DeleteButton onClick={onConfirm}>
            Delete {isBulk ? `(${itemCount})` : ''}
          </DeleteButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
}

export default DeleteConfirmationModal;
